import React, { Component } from "react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import {
  company,
  loading,
  NavbarWarehouse,
  Modal,
  alert,
  GET,
  POST,
  t,
} from "../../components/CustomComponent.js";

import locationTemplateFile from "../../assets/template/12_Location_Template.xlsx";
import warehouseTemplateFile from "../../assets/template/11_Warehouse_Template.xlsx";
import uomTemplateFile from "../../assets/template/07_UnitOfMeasure_Template.xlsx";
import categoryTemplateFile from "../../assets/template/08_Category_Template.xlsx";
import brandTemplateFile from "../../assets/template/04_Brand_Template.xlsx";
import modelTemplateFile from "../../assets/template/05.1_Model_Template.xlsx";
import seriesTemplateFile from "../../assets/template/05.2_Series_Template.xlsx";
import partTemplateFile from "../../assets/template/09_Item_Master_Template.xlsx";
import customerTemplateFile from "../../assets/template/01_Customer_Template.xlsx";

/**
 * Column definitions for the Location data set.
 * `type`:
 *   auto   -> running number generated by the system (not validated / not imported)
 *   text   -> free text
 *   enum   -> must match one of `options` (matched by english key or thai label)
 *   number -> decimal number (optional)
 *   status -> เปิดใช้งาน / ปิดใช้งาน  ->  boolean
 */
const LOCATION_COLUMNS = [
  { key: "no", th: "ลำดับที่", en: "No.", type: "auto" },
  { key: "location_code", th: "รหัสตำแหน่ง", en: "Location Code", type: "text", required: true, unique: true },
  { key: "name", th: "ชื่อตำแหน่ง", en: "Location Name", type: "text", required: true },
  { key: "location_type", th: "ประเภทตำแหน่ง", en: "Location Type", type: "enum", required: true, options: ["zone", "row", "rack", "room", "other"] },
  { key: "address", th: "ที่อยู่ / รายละเอียด", en: "Address / Detail", type: "text" },
  { key: "latitude", th: "ละติจูด", en: "Latitude", type: "number" },
  { key: "longitude", th: "ลองจิจูด", en: "Longitude", type: "number" },
  { key: "description", th: "คำอธิบาย", en: "Description", type: "text" },
  { key: "is_active", th: "สถานะ", en: "Status", type: "status", required: true },
];

/**
 * Column definitions for the Warehouse data set (Template No. 11).
 * `branch` is a Lookup column — valid values come from the "Reference" sheet
 * (stored verbatim via enumKeyMode "label", e.g. "BPSB").
 */
const WAREHOUSE_COLUMNS = [
  { key: "no", th: "ลำดับที่", en: "No.", type: "auto" },
  { key: "branch", th: "สาขา", en: "Branch", type: "enum", required: true, enumKeyMode: "label" },
  { key: "warehouse_code", th: "รหัสคลังสินค้า", en: "Warehouse Code", type: "text", required: true, unique: true },
  { key: "name", th: "ชื่อคลังสินค้า", en: "Warehouse Name", type: "text", required: true },
  { key: "description", th: "คำอธิบาย", en: "Description", type: "text" },
  { key: "storage_locations", th: "พื้นที่เก็บ", en: "Storage Locations", type: "list" },
  { key: "is_active", th: "สถานะ", en: "Status", type: "status", required: true },
];

/**
 * Column definitions for the Unit of Measure data set (Template No. 07).
 * No lookup/enum columns — status accepts Active/Inactive (เปิดใช้งาน/ปิดใช้งาน).
 */
const UOM_COLUMNS = [
  { key: "no", th: "ลำดับที่", en: "Row No.", type: "auto" },
  { key: "uom_code", th: "รหัสหน่วยนับ", en: "Unit Code", type: "text", required: true, unique: true },
  { key: "name", th: "ชื่อหน่วยนับ", en: "Unit Name", type: "text", required: true },
  { key: "symbol", th: "สัญลักษณ์", en: "Symbol", type: "text" },
  { key: "description", th: "คำอธิบาย", en: "Description", type: "text" },
  { key: "is_active", th: "สถานะ", en: "Status", type: "status", required: true },
];

/**
 * Column definitions for the Category data set (Template No. 08).
 * Column headers are on row 8 (row 7 holds group titles) -> data starts at row 9.
 * Part Control columns (E-H) are Yes/No flags mapped into a `field_control` array.
 */
const CATEGORY_COLUMNS = [
  { key: "no", th: "ลำดับที่", en: "Row No.", type: "auto" },
  { key: "category_code", th: "รหัสหมวดหมู่", en: "Category Code", type: "text", required: true, unique: true },
  { key: "name", th: "ชื่อหมวดหมู่", en: "Category Name", type: "text", required: true },
  { key: "description", th: "คำอธิบาย", en: "Description", type: "text" },
  { key: "warehouse_control", th: "Warehouse", en: "Warehouse Control", type: "bool" },
  { key: "location_control", th: "Location", en: "Location Control", type: "bool" },
  { key: "batch_control", th: "Batch Number", en: "Batch Number Control", type: "bool" },
  { key: "date_control", th: "Date", en: "Date Control", type: "bool" },
  { key: "is_active", th: "สถานะ", en: "Status", type: "status", required: true },
];

/**
 * Column definitions for the Brand data set (Template No. 04).
 * `parent_brand` is the parent brand NAME (empty -> level 1, otherwise level 2).
 * Parent is validated against existing level-1 brands + level-1 rows in the same file.
 */
const BRAND_COLUMNS = [
  { key: "no", th: "ลำดับที่", en: "Row No.", type: "auto" },
  { key: "brand_code", th: "รหัสแบรนด์", en: "Brand Code", type: "text", required: true, unique: true },
  { key: "name", th: "ชื่อแบรนด์", en: "Brand Name", type: "text", required: true },
  { key: "parent_brand", th: "แบรนด์หลัก", en: "Parent Brand", type: "text" },
  { key: "description", th: "คำอธิบาย", en: "Description", type: "text" },
  { key: "is_active", th: "สถานะ", en: "Status", type: "status", required: true },
];

/**
 * Column definitions for the Model data set (Template No. 05.1).
 * `brand_code` is a Lookup against the company's brands (resolved to brand_id on save).
 * `brand_name` is an auto VLOOKUP column -> ignored on import (filled on export).
 */
const MODEL_COLUMNS = [
  { key: "no", th: "ลำดับที่", en: "Row No.", type: "auto" },
  { key: "model_code", th: "รหัสรุ่น", en: "Model Code", type: "text", required: true, unique: true },
  { key: "name", th: "ชื่อรุ่น", en: "Model Name", type: "text", required: true },
  { key: "brand_code", th: "รหัสยี่ห้อ", en: "Brand Code", type: "text", required: true },
  { key: "brand_name", th: "ชื่อยี่ห้อ", en: "Brand Name (Auto)", type: "auto" },
  { key: "description", th: "คำอธิบาย", en: "Description", type: "text" },
  { key: "is_active", th: "สถานะ", en: "Status", type: "status", required: true },
];

/**
 * Column definitions for the Series data set (Template No. 05.2).
 * No code field — upsert is by `name` (the unique key).
 */
const SERIES_COLUMNS = [
  { key: "no", th: "ลำดับที่", en: "Row No.", type: "auto" },
  { key: "name", th: "ชื่อซีรีส์", en: "Series Name", type: "text", required: true, unique: true },
  { key: "is_active", th: "สถานะ", en: "Status", type: "status", required: true },
];

/**
 * Column definitions for the Part / Item Master data set (Template No. 09).
 * Headers are on row 8 (row 7 = group titles) -> data starts at row 9.
 * Columns must stay in exact A..AA order so cell indices line up with the sheet.
 * Lookup codes (brand/model/series/category/uom) are resolved to *_id + *_name on save.
 * The *_name columns are auto VLOOKUP (ignored on import); Z/AA are not imported.
 */
const PART_COLUMNS = [
  { key: "no", th: "ลำดับที่", en: "No.", type: "auto" },                                   // A
  { key: "part_number", th: "รหัสอะไหล่", en: "Part ID", type: "text", required: true }, // B (duplicate -> upsert: existing updates, new creates)
  { key: "barcode", th: "Barcode", en: "Barcode", type: "text" },                            // C
  { key: "name", th: "คำอธิบาย", en: "Description", type: "text", required: true },           // D
  { key: "brand_code", th: "รหัสแบรนด์", en: "Brand ID", type: "text" },                      // E (lookup)
  { key: "brand_name", th: "ชื่อแบรนด์", en: "Brand Name", type: "auto" },                    // F
  { key: "model_code", th: "รหัสรุ่น", en: "Model ID", type: "text" },                        // G (lookup)
  { key: "model_name", th: "ชื่อรุ่น", en: "Model Name", type: "auto" },                      // H
  { key: "serie_name", th: "รหัสซีรีส์", en: "Series ID", type: "text" },                     // I (lookup by name)
  { key: "serie_name_auto", th: "ชื่อซีรีส์", en: "Series Name", type: "auto" },              // J
  { key: "category_code_1", th: "รหัสหมวดหมู่ 1", en: "Category ID 1", type: "text", required: true }, // K (lookup)
  { key: "category_name_1", th: "ชื่อหมวดหมู่ 1", en: "Category Name 1", type: "auto" },       // L
  { key: "category_code_2", th: "รหัสหมวดหมู่ 2", en: "Category ID 2", type: "text" },         // M (lookup)
  { key: "category_name_2", th: "ชื่อหมวดหมู่ 2", en: "Category Name 2", type: "auto" },       // N
  { key: "category_code_3", th: "รหัสหมวดหมู่ 3", en: "Category ID 3", type: "text" },         // O (lookup)
  { key: "category_name_3", th: "ชื่อหมวดหมู่ 3", en: "Category Name 3", type: "auto" },       // P
  { key: "uom_code", th: "รหัสหน่วยนับ", en: "Unit ID", type: "text", required: true },        // Q (lookup)
  { key: "uom_name", th: "ชื่อหน่วยนับ", en: "Unit Name", type: "auto" },                      // R
  { key: "uom_cost", th: "ต้นทุนต่อหน่วย", en: "Unit Cost", type: "number" },                  // S
  { key: "selling_price", th: "ราคาขาย", en: "Selling Price", type: "number" },               // T
  { key: "min_stock", th: "สต็อกขั้นต่ำ", en: "Min Stock", type: "number" },                   // U
  { key: "reorder_point", th: "จุดสั่งซื้อ", en: "Reorder Point", type: "number" },            // V
  { key: "reorder_qty", th: "ปริมาณสั่งซื้อ", en: "Reorder Qty", type: "number" },             // W
  { key: "lead_time_days", th: "ระยะเวลารอ (วัน)", en: "Lead Time (Days)", type: "number" },   // X
  { key: "supplier_name", th: "ผู้จำหน่ายหลัก", en: "Main Supplier", type: "text" },           // Y
  { key: "legacy_ref", th: "อ้างอิงอะไหล่เก่า", en: "Reference Old Part", type: "auto" },       // Z (not imported)
  { key: "compatible_series", th: "ซีรีส์ที่รองรับ", en: "Compatible Series", type: "auto" },   // AA (not imported)
];

/**
 * Column definitions for the Customer data set (Template No. 01).
 * Sheet "Customer Template" — headers on row 7, data starts at row 8 (A..AK).
 * Column order MUST match the template sheet exactly (cells are read by position).
 * Enum columns (type/group/source/contact_prefix/payment_type/currency/sla_level) use
 * `enumKeyMode: "label"` so the value is stored verbatim and matched against the
 * "Reference" sheet (which lists value in column B -> reference.valueColumn = 1).
 * `sla_level` is sent as text and resolved to priority_id (work_requisition_priorities.name_en)
 * on the backend. Status maps to both customer_status (string) and is_active (boolean).
 * All columns map to existing `customers` model fields — no new model fields are added.
 */
const CUSTOMER_COLUMNS = [
  { key: "no", th: "ลำดับที่", en: "Row No.", type: "auto" },                                            // A
  { key: "customer_code", th: "รหัสลูกค้า", en: "Customer Code", type: "text", required: true, unique: true }, // B
  { key: "full_name_th", th: "ชื่อลูกค้า (ไทย)", en: "Customer Name TH", type: "text", required: true },  // C
  { key: "alias_name", th: "ชื่อย่อ", en: "Alias", type: "text" },                                        // D
  { key: "full_name_en", th: "ชื่อลูกค้า (อังกฤษ)", en: "Customer Name EN", type: "text" },              // E
  { key: "type", th: "ประเภทลูกค้า", en: "Customer Type", type: "enum", required: true, enumKeyMode: "label" }, // F
  { key: "group", th: "กลุ่มลูกค้า", en: "Customer Group", type: "enum", enumKeyMode: "label" },          // G
  { key: "tax_number", th: "เลขผู้เสียภาษี", en: "Tax ID", type: "text" },                                // H
  { key: "branch_type", th: "สำนักงานใหญ่ / สาขา", en: "HQ / Branch", type: "text" },                     // I
  { key: "branch_code", th: "รหัสสาขา", en: "Branch Code", type: "text" },                                // J
  { key: "delivery_zone", th: "ชื่อเขตลูกค้า / โซนส่งของ", en: "Delivery Zone", type: "text" },           // K
  { key: "active_start_date", th: "วันที่เริ่มเป็นลูกค้า", en: "Start Date", type: "date" },              // L
  { key: "source", th: "แหล่งที่มา", en: "Source", type: "enum", enumKeyMode: "label" },                  // M
  { key: "address", th: "ที่อยู่", en: "Address", type: "text" },                                          // N
  { key: "sub_district", th: "ตำบล/แขวง", en: "Sub-district", type: "text" },                             // O
  { key: "district", th: "อำเภอ/เขต", en: "District", type: "text" },                                     // P
  { key: "province", th: "จังหวัด", en: "Province", type: "text" },                                        // Q
  { key: "zip_code", th: "รหัสไปรษณีย์", en: "Postal Code", type: "text" },                               // R
  { key: "phone", th: "โทรศัพท์ 1", en: "Phone 1", type: "text" },                                         // S
  { key: "phone_2", th: "โทรศัพท์ 2", en: "Phone 2", type: "text" },                                      // T
  { key: "fax", th: "โทรสาร", en: "Fax", type: "text" },                                                  // U
  { key: "mobile_phone", th: "โทรศัพท์มือถือ", en: "Mobile", type: "text" },                              // V
  { key: "email", th: "อีเมล", en: "Email", type: "email" },                                              // W
  { key: "website", th: "เว็บไซต์", en: "Website", type: "text" },                                        // X
  { key: "contact_prefix", th: "คำนำหน้า", en: "Title", type: "enum", enumKeyMode: "label" },             // Y
  { key: "contact_first_name", th: "ชื่อผู้ติดต่อหลัก", en: "Primary Contact", type: "text" },             // Z
  { key: "contact_last_name", th: "นามสกุล", en: "Last Name", type: "text" },                             // AA
  { key: "contact_position", th: "ตำแหน่ง", en: "Position", type: "text" },                               // AB
  { key: "contact_phone", th: "โทรศัพท์ผู้ติดต่อ", en: "Contact Phone", type: "text" },                    // AC
  { key: "contact_email", th: "อีเมลผู้ติดต่อ", en: "Contact Email", type: "email" },                     // AD
  { key: "payment_type", th: "วิธีการชำระเงิน", en: "Payment Method", type: "enum", enumKeyMode: "label" }, // AE
  { key: "currency", th: "สกุลเงิน", en: "Currency", type: "enum", enumKeyMode: "label" },                // AF
  { key: "payment_terms", th: "เงื่อนไขชำระเงิน", en: "Payment Terms", type: "text" },                    // AG
  { key: "sla_level", th: "ระดับ SLA เริ่มต้น", en: "SLA Level", type: "enum", enumKeyMode: "label" },    // AH
  { key: "account_owner", th: "ผู้ดูแลบัญชี", en: "Account Owner", type: "text" },                        // AI
  { key: "remark", th: "หมายเหตุ", en: "Remarks", type: "text" },                                         // AJ
  // Tri-state status (Active / Inactive / Hold) -> stored verbatim in customer_status.
  { key: "customer_status", th: "สถานะ", en: "Status", type: "customer_status" },                          // AK
];

/**
 * Per-data-set import/export configuration.
 * Only `location` is fully wired for now — the remaining data sets fall back to a
 * "not available yet" notice until their templates / endpoints are ready.
 */
const DATASET_CONFIG = {
  customer: {
    endpoint: "admin/customers",
    importPath: "admin/customers/import", // dedicated bulk upsert by customer_code
    templateAsset: customerTemplateFile,
    templateFile: "01_Customer_Template.xlsx",
    exportFile: "Customer_Export.xlsx",
    sheetName: "Customer Template",
    headerRows: 7, // rows 1-7 are header/instructions; data starts at row 8
    columns: CUSTOMER_COLUMNS,
    // Reference sheet lists values in column B (column A is the running No.) -> valueColumn 1.
    reference: {
      sheetMatch: "Reference",
      valueColumn: 1,
      sections: [
        { columnKey: "type", startsWith: "ประเภทลูกค้า" },
        { columnKey: "group", startsWith: "กลุ่มลูกค้า" },
        { columnKey: "source", startsWith: "แหล่งที่มา" },
        { columnKey: "contact_prefix", startsWith: "คำนำหน้า" },
        { columnKey: "payment_type", startsWith: "วิธีการชำระเงิน" },
        { columnKey: "currency", startsWith: "สกุลเงิน" },
        { columnKey: "sla_level", startsWith: "ระดับ SLA" },
      ],
    },
    // API record -> row aligned to `columns` (display labels match the template/Reference).
    toRow: (item, idx) => [
      idx + 1,
      item.customer_code || "",
      item.full_name_th || "",
      item.alias_name || "",
      item.full_name_en || "",
      item.type || "",
      item.group || "",
      item.tax_number || "",
      item.branch_type || "",
      item.branch_code || "",
      item.delivery_zone || "",
      item.active_start_date || "",
      item.source || "",
      item.address || "",
      item.sub_district || "",
      item.district || "",
      item.province || "",
      item.zip_code || "",
      item.phone || "",
      item.phone_2 || "",
      item.fax || "",
      item.mobile_phone || "",
      item.email || "",
      item.website || "",
      item.contact_prefix || "",
      item.contact_first_name || "",
      item.contact_last_name || "",
      item.contact_position || "",
      item.contact_phone || "",
      item.contact_email || "",
      item.payment_type || "",
      item.currency || "",
      item.payment_terms || "",
      item.priority?.name_en || "",
      item.account_owner || "",
      item.remark || "",
      item.customer_status || (item.is_active ? "Active" : "Inactive"),
    ],
    // validated cells -> upsert payload (priority resolved from sla_level on the backend)
    toPayload: (cells) => ({
      company_id: company?.id || "",
      customer_code: cells.customer_code.value,
      full_name_th: cells.full_name_th.value,
      full_name_en: cells.full_name_en?.value || "",
      alias_name: cells.alias_name?.value || "",
      type: cells.type.value,
      group: cells.group?.value || "",
      tax_number: cells.tax_number?.value || "",
      branch_type: cells.branch_type?.value || "",
      branch_code: cells.branch_code?.value || "",
      delivery_zone: cells.delivery_zone?.value || "",
      active_start_date: cells.active_start_date?.value || "",
      source: cells.source?.value || "",
      address: cells.address.value,
      sub_district: cells.sub_district?.value || "",
      district: cells.district?.value || "",
      province: cells.province.value,
      zip_code: cells.zip_code?.value || "",
      phone: cells.phone?.value || "",
      phone_2: cells.phone_2?.value || "",
      fax: cells.fax?.value || "",
      mobile_phone: cells.mobile_phone?.value || "",
      email: cells.email?.value || "",
      website: cells.website?.value || "",
      contact_prefix: cells.contact_prefix?.value || "",
      contact_first_name: cells.contact_first_name.value,
      contact_last_name: cells.contact_last_name?.value || "",
      contact_position: cells.contact_position?.value || "",
      contact_phone: cells.contact_phone?.value || "",
      contact_email: cells.contact_email?.value || "",
      payment_type: cells.payment_type?.value || "",
      currency: cells.currency?.value || "",
      payment_terms: cells.payment_terms?.value || "",
      sla_level: cells.sla_level?.value || "",
      account_owner: cells.account_owner?.value || "",
      remark: cells.remark?.value || "",
      customer_status: cells.customer_status.value, // "Active" | "Inactive" | "Hold"
      is_active: cells.customer_status.value === "Active",
    }),
  },

  location: {
    endpoint: "warehouse/locations",
    importPath: "warehouse/locations/import", // bulk upsert endpoint (implemented)
    templateAsset: locationTemplateFile,
    templateFile: "12_Location_Template.xlsx",
    exportFile: "Location_Export.xlsx",
    sheetName: "Data Template",
    headerRows: 7, // rows 1-7 are header/instructions; data starts at row 8
    columns: LOCATION_COLUMNS,
    // Reference sheet — valid values for enum/status columns are read from here.
    reference: {
      sheetMatch: "Reference",
      sections: [
        { columnKey: "location_type", startsWith: "ประเภทตำแหน่ง" },
        { columnKey: "is_active", startsWith: "สถานะ" },
      ],
    },
    // API record -> row aligned to `columns` (use display labels so it matches the template/Reference)
    toRow: (item, idx) => [
      idx + 1,
      item.location_code || "",
      item.name || "",
      item.location_type ? t(item.location_type) : "",
      item.address || "",
      item.latitude || "",
      item.longitude || "",
      item.description || "",
      item.is_active ? "เปิดใช้งาน" : "ปิดใช้งาน",
    ],
    // validated cells -> create payload
    toPayload: (cells) => ({
      company_id: company?.id || "",
      location_code: cells.location_code.value,
      name: cells.name.value,
      location_type: cells.location_type.value,
      address: cells.address?.value || "",
      latitude: cells.latitude?.value ? String(cells.latitude.value) : "",
      longitude: cells.longitude?.value ? String(cells.longitude.value) : "",
      description: cells.description?.value || "",
      is_active: cells.is_active.value === true,
    }),
  },

  warehouse: {
    endpoint: "warehouse/warehouses",
    importPath: "warehouse/warehouses/import", // bulk upsert + links storage locations
    templateAsset: warehouseTemplateFile,
    templateFile: "11_Warehouse_Template.xlsx",
    exportFile: "Warehouse_Export.xlsx",
    sheetName: "Data Template",
    headerRows: 7,
    columns: WAREHOUSE_COLUMNS,
    reference: {
      sheetMatch: "Reference",
      sections: [
        { columnKey: "branch", startsWith: "สาขา" },
        { columnKey: "is_active", startsWith: "สถานะ" },
      ],
    },
    // Lookups fetched (company-scoped) before validation -> { columnKey: { endpoint, field } }.
    lookups: {
      storage_locations: { endpoint: "warehouse/locations", field: "location_code" },
    },
    toRow: (item, idx) => [
      idx + 1,
      item.branch || "",
      item.warehouse_code || "",
      item.name || "",
      item.description || "",
      (item.locations || []).map((l) => l.location_code).join(", "),
      item.is_active ? "เปิดใช้งาน" : "ปิดใช้งาน",
    ],
    toPayload: (cells) => ({
      company_id: company?.id || "",
      branch: cells.branch.value,
      warehouse_code: cells.warehouse_code.value,
      name: cells.name.value,
      description: cells.description?.value || "",
      storage_locations: Array.isArray(cells.storage_locations?.value)
        ? cells.storage_locations.value
        : [],
      is_active: cells.is_active.value === true,
    }),
  },

  uom: {
    endpoint: "warehouse/uoms",
    importPath: "warehouse/uoms/import", // bulk upsert by uom_code
    templateAsset: uomTemplateFile,
    templateFile: "07_UnitOfMeasure_Template.xlsx",
    exportFile: "UnitOfMeasure_Export.xlsx",
    sheetName: "Unit Template",
    headerRows: 7,
    columns: UOM_COLUMNS,
    toRow: (item, idx) => [
      idx + 1,
      item.uom_code || "",
      item.name || "",
      item.symbol || "",
      item.description || "",
      item.is_active ? "Active" : "Inactive",
    ],
    toPayload: (cells) => ({
      company_id: company?.id || "",
      uom_code: cells.uom_code.value,
      name: cells.name.value,
      symbol: cells.symbol?.value || "",
      description: cells.description?.value || "",
      is_active: cells.is_active.value === true,
    }),
  },

  category: {
    endpoint: "warehouse/part-categories",
    importPath: "warehouse/part-categories/import", // bulk upsert by category_code
    templateAsset: categoryTemplateFile,
    templateFile: "08_Category_Template.xlsx",
    exportFile: "Category_Export.xlsx",
    sheetName: "Category Template",
    headerRows: 8, // row 7 = group titles, row 8 = column headers -> data from row 9
    columns: CATEGORY_COLUMNS,
    toRow: (item, idx) => {
      const fc = Array.isArray(item.field_control) ? item.field_control : [];
      const yn = (k) => (fc.includes(k) ? "Yes" : "No");
      return [
        idx + 1,
        item.category_code || "",
        item.name || "",
        item.description || "",
        yn("from_warehouse_id"),
        yn("location_id"),
        yn("lot_id"),
        yn("lot_received_date"),
        item.is_active ? "Active" : "Inactive",
      ];
    },
    toPayload: (cells) => {
      const field_control = [];
      if (cells.warehouse_control?.value === true) field_control.push("from_warehouse_id");
      if (cells.location_control?.value === true) field_control.push("location_id");
      if (cells.batch_control?.value === true) field_control.push("lot_id");
      if (cells.date_control?.value === true) field_control.push("lot_received_date");
      return {
        company_id: company?.id || "",
        category_code: cells.category_code.value,
        name: cells.name.value,
        description: cells.description?.value || "",
        field_control,
        is_active: cells.is_active.value === true,
      };
    },
  },

  brand: {
    endpoint: "warehouse/brands",
    importPath: "warehouse/brands/import", // bulk upsert by brand_code + parent/level resolution
    templateAsset: brandTemplateFile,
    templateFile: "04_Brand_Template.xlsx",
    exportFile: "Brand_Export.xlsx",
    sheetName: "Brand Template",
    headerRows: 7,
    columns: BRAND_COLUMNS,
    // Parent brand candidates = existing level-1 brand names (case-insensitive).
    lookups: {
      parent_brand: { endpoint: "warehouse/brands", field: "name", filter: (d) => Number(d.level) === 1, lower: true },
    },
    // Cross-row check: a parent must be an existing level-1 brand OR a level-1 row in this file.
    postValidate: (rows, lookupSets) => {
      const existing = lookupSets.parent_brand || new Set();
      const inFile = new Set();
      rows.forEach((r) => {
        const parent = (r.cells.parent_brand?.raw || "").trim();
        const name = (r.cells.name?.raw || "").trim();
        if (!parent && name) inFile.add(name.toLowerCase());
      });
      rows.forEach((r) => {
        const cell = r.cells.parent_brand;
        if (!cell) return;
        const p = (cell.raw || "").trim();
        if (!p || cell.error) return;
        if (!existing.has(p.toLowerCase()) && !inFile.has(p.toLowerCase())) {
          cell.error = "parent_not_found";
          cell.detail = p;
          r.valid = false;
        }
      });
      return rows;
    },
    // Children reference a parent row in the same file -> import in order, one chunk
    // at a time, with level-1 (no parent) rows first so each parent is committed
    // before the children that reference it.
    sequentialImport: true,
    sortRowsForImport: (rows) =>
      [...rows].sort(
        (a, b) =>
          (String(a.parent || "").trim() ? 1 : 0) - (String(b.parent || "").trim() ? 1 : 0)
      ),
    // Nested (tops + children) -> flat rows; a child row carries its parent's name.
    flattenForExport: (data) => {
      const out = [];
      (data || []).forEach((top) => {
        out.push({ ...top, _parent_name: "" });
        (top.children || []).forEach((ch) => out.push({ ...ch, _parent_name: top.name }));
      });
      return out;
    },
    toRow: (item, idx) => [
      idx + 1,
      item.brand_code || "",
      item.name || "",
      item._parent_name || "",
      item.description || "",
      item.is_active ? "Active" : "Inactive",
    ],
    toPayload: (cells) => ({
      company_id: company?.id || "",
      brand_code: cells.brand_code.value,
      name: cells.name.value,
      parent: cells.parent_brand?.value || "",
      description: cells.description?.value || "",
      is_active: cells.is_active.value === true,
    }),
  },

  model: {
    endpoint: "warehouse/models",
    importPath: "warehouse/models/import", // bulk upsert by model_code + brand_code -> brand_id
    templateAsset: modelTemplateFile,
    templateFile: "05.1_Model_Template.xlsx",
    exportFile: "Model_Export.xlsx",
    sheetName: "Model Template",
    headerRows: 7,
    columns: MODEL_COLUMNS,
    // brand_code must reference a real brand (any level). Brand tree is nested -> flatten.
    lookups: {
      brand_code: {
        endpoint: "warehouse/brands",
        field: "brand_code",
        flatten: (data) => (data || []).flatMap((b) => [b, ...(b.children || [])]),
      },
    },
    toRow: (item, idx) => [
      idx + 1,
      item.model_code || "",
      item.name || "",
      item.brand?.brand_code || "",
      item.brand?.name || "",
      item.description || "",
      item.is_active ? "Active" : "Inactive",
    ],
    toPayload: (cells) => ({
      company_id: company?.id || "",
      model_code: cells.model_code.value,
      name: cells.name.value,
      brand_code: cells.brand_code.value,
      description: cells.description?.value || "",
      is_active: cells.is_active.value === true,
    }),
  },

  series: {
    endpoint: "warehouse/series",
    importPath: "warehouse/series/import", // bulk upsert by name (no code field)
    templateAsset: seriesTemplateFile,
    templateFile: "05.2_Series_Template.xlsx",
    exportFile: "Series_Export.xlsx",
    sheetName: "Series Template",
    headerRows: 7,
    columns: SERIES_COLUMNS,
    toRow: (item, idx) => [
      idx + 1,
      item.name || "",
      item.is_active ? "Active" : "Inactive",
    ],
    toPayload: (cells) => ({
      company_id: company?.id || "",
      name: cells.name.value,
      is_active: cells.is_active.value === true,
    }),
  },

  part_master: {
    endpoint: "warehouse/part-masters",
    importPath: "warehouse/part-masters/import", // bulk upsert by part_number + resolve relations
    templateAsset: partTemplateFile,
    templateFile: "09_Item_Master_Template.xlsx",
    exportFile: "ItemMaster_Export.xlsx",
    sheetName: "Data Template",
    headerRows: 8, // row 7 = group titles, row 8 = column headers -> data from row 9
    columns: PART_COLUMNS,
    // All lookups are company-scoped. Categories share one fetch (endpoint cache).
    lookups: {
      brand_code: { endpoint: "warehouse/brands", field: "brand_code", flatten: (d) => (d || []).flatMap((b) => [b, ...(b.children || [])]) },
      model_code: { endpoint: "warehouse/models", field: "model_code" },
      serie_name: { endpoint: "warehouse/series", field: "name" },
      category_code_1: { endpoint: "warehouse/part-categories", field: "category_code" },
      category_code_2: { endpoint: "warehouse/part-categories", field: "category_code" },
      category_code_3: { endpoint: "warehouse/part-categories", field: "category_code" },
      uom_code: { endpoint: "warehouse/uoms", field: "uom_code" },
    },
    toRow: (item, idx) => [
      idx + 1,
      item.part_number || "",
      item.barcode || "",
      item.name || "",
      item.brand?.brand_code || "",
      item.brand?.name || "",
      item.model?.model_code || "",
      item.model?.name || "",
      item.serie?.name || "",
      item.serie?.name || "",
      item.part_category?.category_code || "",
      item.part_category?.name || "",
      item.part_category_2?.category_code || "",
      item.part_category_2?.name || "",
      item.part_category_3?.category_code || "",
      item.part_category_3?.name || "",
      item.uom?.uom_code || "",
      item.uom?.name || "",
      item.uom_cost ?? "",
      item.selling_price ?? "",
      item.min_stock ?? "",
      item.reorder_point ?? "",
      item.reorder_qty ?? "",
      item.lead_time_days ?? "",
      item.supplier?.name || item.supplier_name || "",
      item.legacy_part?.part_number || "",
      (item.compatible_series || []).map((s) => s.name).join(", "),
    ],
    toPayload: (cells) => {
      const num = (k) => {
        const v = cells[k]?.value;
        return v !== "" && v != null && isFinite(Number(v)) ? Number(v) : null;
      };
      const int = (k) => {
        const n = num(k);
        return n == null ? 0 : Math.trunc(n);
      };
      const money = (k) => {
        const n = num(k);
        return n == null ? null : Math.round(n * 100) / 100;
      };
      return {
        company_id: company?.id || "",
        part_number: cells.part_number.value,
        name: cells.name.value,
        description: cells.name.value, // template has a single Description field -> name + description
        barcode: cells.barcode?.value || "",
        brand_code: cells.brand_code?.value || "",
        model_code: cells.model_code?.value || "",
        serie_name: cells.serie_name?.value || "",
        category_code_1: cells.category_code_1.value,
        category_code_2: cells.category_code_2?.value || "",
        category_code_3: cells.category_code_3?.value || "",
        uom_code: cells.uom_code.value,
        uom_cost: money("uom_cost"),
        selling_price: money("selling_price"),
        min_stock: int("min_stock"),
        reorder_point: int("reorder_point"),
        reorder_qty: int("reorder_qty"),
        lead_time_days: int("lead_time_days"),
        supplier_name: cells.supplier_name?.value || "",
        is_active: cells.is_active?.value ?? true,
      };
    },
  },
};

/**
 * Rows are POSTed to the import endpoint in chunks so the UI can show real
 * completion %. To keep it fast, chunks are sent with bounded concurrency
 * (IMPORT_CONCURRENCY at a time) instead of strictly one-after-another.
 * Each chunk is its own backend transaction; the whole-file client validation
 * (unique codes) keeps disjoint chunks conflict-free. Data sets with intra-file
 * dependencies (brand: child rows reference a parent row in the same file) set
 * `sequentialImport: true` and provide `sortRowsForImport` so parents are
 * always committed before the children that reference them.
 */
const IMPORT_CHUNK_SIZE = 100;
const IMPORT_CONCURRENCY = 4;

export default class ImportCenter extends Component {
  constructor(props) {
    super(props);
    this.fileInputRef = React.createRef();
    this.state = {
      filter_search: "",

      // Import preview modal
      import_modal: false,
      active_key: "",
      import_rows: [], // [{ cells: { key: { raw, value, error } }, valid }]
      importing: false, // true while import requests are in flight
      import_progress: 0, // 0-100 completion % across all chunks
      import_done: 0, // rows committed so far (for the "x / total" hint)
      show_invalid_only: false, // preview table: filter to error rows only

      // Data set catalog (UI list)
      data_sets: [
        { key: "customer", name: "import_customer", description: "import_customer_desc", template: "01_Customer_Template.xlsx", icon: "", color: "primary" },
        { key: "part_master", name: "import_part_master", description: "import_part_master_desc", template: "09_Item_Master_Template.xlsx", icon: "", color: "primary" },
        { key: "warehouse", name: "import_warehouse", description: "import_warehouse_desc", template: "11_Warehouse_Template.xlsx", icon: "", color: "primary" },
        { key: "location", name: "import_location", description: "import_location_desc", template: "12_Location_Template.xlsx", icon: "", color: "primary" },
        { key: "uom", name: "import_uom", description: "import_uom_desc", template: "07_UnitOfMeasure_Template.xlsx", icon: "", color: "primary" },
        { key: "category", name: "import_category", description: "import_category_desc", template: "08_Category_Template.xlsx", icon: "", color: "primary" },
        { key: "brand", name: "import_brand", description: "import_brand_desc", template: "04_Brand_Template.xlsx", icon: "", color: "primary" },
        { key: "model", name: "import_model", description: "import_model_desc", template: "05.1_Model_Template.xlsx", icon: "", color: "primary" },
        { key: "series", name: "import_series", description: "import_series_desc", template: "05.2_Series_Template.xlsx", icon: "", color: "primary" },
      ],
    };
  }

  async componentDidMount() {
    loading(false);
  }

  // ---- Helpers -------------------------------------------------------------

  // Clear any leftover import preview / state. Called at the start of every action.
  resetImport = () => {
    this.setState({ import_modal: false, active_key: "", import_rows: [], importing: false, import_progress: 0, import_done: 0, show_invalid_only: false });
  };

  // Read the "Reference" sheet of the uploaded file -> { columnKey: [allowed labels] }
  parseReference = (wb, cfg) => {
    const refMap = {};
    if (!cfg.reference) return refMap;

    const sheetName = wb.SheetNames.find((n) => n.includes(cfg.reference.sheetMatch));
    if (!sheetName) return refMap;

    // Section titles always live in column A; the allowed value may live in a
    // later column (e.g. customer Reference has "No." in A and the value in B).
    const valueCol = cfg.reference.valueColumn || 0;
    const rows = XLSX.utils.sheet_to_json(wb.Sheets[sheetName], { header: 1, defval: "" });
    let current = null;
    rows.forEach((row) => {
      const head = String(row[0] ?? "").trim();
      // A "N. Title" line (or one of our section titles) is a section boundary.
      const isSectionHeader = /^\d+\s*\./.test(head) || cfg.reference.sections.some((s) => head.includes(s.startsWith));
      if (isSectionHeader) {
        const sec = cfg.reference.sections.find((s) => head.includes(s.startsWith));
        current = sec ? sec.columnKey : null; // unknown section -> stop collecting
        if (current) refMap[current] = [];
        return;
      }
      if (!current) return;
      const val = String(row[valueCol] ?? "").trim();
      if (!val) return;
      // skip the "ค่า (Value)" column-header row
      if (val.includes("ค่า") || val.toLowerCase().includes("value")) return;
      refMap[current].push(val);
    });
    return refMap;
  };

  // Derive the stored value for an enum reference label.
  //   default      "โซน (Zone)" -> "zone"   (extract the english key in parentheses)
  //   mode "label" "BPSB"       -> "BPSB"   (keep the value verbatim, e.g. a branch code)
  enumKeyOf = (col, label) => {
    if (col && col.enumKeyMode === "label") return String(label ?? "").trim();
    const m = String(label ?? "").match(/\(([^)]+)\)/);
    return (m ? m[1] : String(label ?? "")).trim().toLowerCase();
  };

  // Candidate {key,label} list for an enum column: prefer the Reference sheet, fall back to config options.
  enumCandidates = (col, refMap) => {
    const refValues = refMap && refMap[col.key];
    if (refValues && refValues.length) {
      return refValues.map((label) => ({ key: this.enumKeyOf(col, label), label }));
    }
    return (col.options || []).map((o) => ({ key: o, label: t(o) }));
  };

  // Match a cell value against the candidates by exact key or label (case-insensitive).
  matchEnum = (val, candidates) => {
    const v = String(val ?? "").trim().toLowerCase();
    if (!v) return null;
    for (const c of candidates) {
      const k = String(c.key).trim().toLowerCase();
      const l = String(c.label).trim().toLowerCase();
      if (v === k || v === l) return c.key;
    }
    return null;
  };

  normStatus = (val) => {
    const v = String(val ?? "").trim().toLowerCase();
    if (["เปิดใช้งาน", "ใช้งาน", "active", "true", "1"].includes(v)) return true;
    if (["ปิดใช้งาน", "ไม่ใช้งาน", "inactive", "false", "0"].includes(v)) return false;
    return null;
  };

  // Customer status is tri-state -> canonical "Active" | "Inactive" | "Hold" (null = invalid).
  normCustomerStatus = (val) => {
    const v = String(val ?? "").trim().toLowerCase();
    if (["active", "เปิดใช้งาน", "ใช้งาน", "true", "1"].includes(v)) return "Active";
    if (["inactive", "ปิดใช้งาน", "ไม่ใช้งาน", "false", "0"].includes(v)) return "Inactive";
    if (["hold", "on hold", "ระงับ", "พักใช้งาน", "พัก"].includes(v)) return "Hold";
    return null;
  };

  // Yes/No -> boolean. Empty defaults to false (No). Returns null for invalid values.
  normBool = (val) => {
    const v = String(val ?? "").trim().toLowerCase();
    if (v === "") return false;
    if (["yes", "y", "true", "1", "ใช่"].includes(v)) return true;
    if (["no", "n", "false", "0", "ไม่", "ไม่ใช่"].includes(v)) return false;
    return null;
  };

  // Basic email shape check (value is already trimmed by the caller).
  isEmail = (val) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(val ?? "").trim());

  // Accept DD/MM/YYYY (incl. Buddhist year) or anything Date can parse; else null.
  normDate = (val) => {
    const s = String(val ?? "").trim();
    if (!s) return null;
    const m = s.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
    if (m) {
      const [, d, mo] = m;
      const day = Number(d), month = Number(mo);
      if (month < 1 || month > 12 || day < 1 || day > 31) return null;
      return s;
    }
    return Number.isNaN(new Date(s).getTime()) ? null : s;
  };

  validateCell = (col, raw, seen, refMap = {}, lookupSets = {}) => {
    const s = raw === undefined || raw === null ? "" : String(raw).trim();
    let value = s;
    let error = "";
    let detail = "";

    if (col.type === "number") {
      if (s !== "" && !isFinite(Number(s))) error = "number";
    } else if (col.type === "email") {
      if (s === "") {
        if (col.required) error = "required";
      } else if (!this.isEmail(s)) {
        error = "email";
      }
    } else if (col.type === "date") {
      if (s === "") {
        if (col.required) error = "required";
      } else if (this.normDate(s) === null) {
        error = "date";
      }
    } else if (col.type === "enum") {
      if (s === "") {
        if (col.required) error = "required";
      } else {
        const n = this.matchEnum(s, this.enumCandidates(col, refMap));
        if (!n) error = "enum";
        else value = n;
      }
    } else if (col.type === "status") {
      if (s === "") {
        if (col.required) error = "required";
      } else {
        const b = this.normStatus(s);
        if (b === null) error = "status";
        else value = b;
      }
    } else if (col.type === "customer_status") {
      if (s === "") {
        if (col.required) error = "required";
        else value = "Active"; // default missing status to Active
      } else {
        const cs = this.normCustomerStatus(s);
        if (cs === null) error = "customer_status";
        else value = cs; // "Active" | "Inactive" | "Hold"
      }
    } else if (col.type === "bool") {
      // Yes/No flag, optional, defaults to No.
      const b = this.normBool(s);
      if (b === null) error = "bool";
      else value = b;
    } else if (col.type === "list") {
      // Comma-separated codes; optionally checked against a lookup set (e.g. company locations).
      if (s === "") {
        if (col.required) error = "required";
        value = [];
      } else {
        const items = s.split(",").map((x) => x.trim()).filter(Boolean);
        value = items;
        const set = lookupSets[col.key];
        if (set) {
          const missing = items.filter((it) => !set.has(it));
          if (missing.length) {
            error = "lookup";
            detail = missing.join(", ");
          }
        }
      }
    } else {
      // text — optionally checked against a single-value lookup set (e.g. brand_code).
      if (col.required && s === "") {
        error = "required";
      } else if (s !== "" && lookupSets[col.key] && !lookupSets[col.key].has(s)) {
        error = "lookup";
        detail = s;
      }
    }

    if (!error && col.unique && s !== "") {
      const k = s.toLowerCase();
      if (seen[k]) error = "duplicate";
      else seen[k] = true;
    }

    return { raw: s, value, error, detail };
  };

  validateRows = (cfg, raw, refMap = {}, lookupSets = {}) => {
    const seen = {};
    const out = [];
    raw.forEach((arr) => {
      // Drop rows with no real data. Ignore the auto running-number column and
      // bool columns left at their default "No" (templates pre-fill these).
      const allEmpty = cfg.columns.every((c, ci) => {
        if (c.type === "auto") return true;
        const v = String(arr[ci] ?? "").trim();
        if (v === "") return true;
        if (c.type === "bool" && this.normBool(v) === false) return true;
        if (c.type === "number" && Number(v) === 0) return true; // templates pre-fill 0
        return false;
      });
      if (allEmpty) return;

      const cells = {};
      let valid = true;
      cfg.columns.forEach((c, ci) => {
        if (c.type === "auto") return;
        const res = this.validateCell(c, arr[ci], seen, refMap, lookupSets);
        cells[c.key] = res;
        if (res.error) valid = false;
      });
      out.push({ cells, valid });
    });
    return out;
  };

  errorText = (code) => {
    switch (code) {
      case "required": return t("import_err_required");
      case "number": return t("import_err_number");
      case "email": return t("import_err_email");
      case "date": return t("import_err_date");
      case "enum": return t("import_err_enum");
      case "status": return t("import_err_status");
      case "customer_status": return t("import_err_customer_status");
      case "bool": return t("import_err_bool");
      case "duplicate": return t("import_err_duplicate");
      case "lookup": return t("import_err_lookup");
      case "parent_not_found": return t("import_err_parent");
      default: return "";
    }
  };

  // Fetch the company-scoped lookup sets declared in cfg.lookups -> { columnKey: Set(codes) }.
  fetchLookupSets = async (cfg) => {
    const lookupSets = {};
    if (!cfg.lookups) return lookupSets;
    const cache = {}; // endpoint -> data[] | null (so categories aren't fetched 3x)
    for (const [colKey, lk] of Object.entries(cfg.lookups)) {
      if (!(lk.endpoint in cache)) {
        const res = await GET(lk.endpoint, { page_size: 100000, page: 0 });
        cache[lk.endpoint] = res?.status ? res.data || [] : null;
      }
      const raw = cache[lk.endpoint];
      if (raw == null) continue; // fetch failed -> skip (avoids false errors)
      const flat = lk.flatten ? lk.flatten(raw) : raw; // e.g. unfold nested brand tree
      const data = lk.filter ? flat.filter(lk.filter) : flat;
      const values = data.map((d) => String(d[lk.field] ?? "").trim()).filter(Boolean);
      // `lower` -> case-insensitive (names); default exact (codes / identifiers).
      lookupSets[colKey] = new Set(lk.lower ? values.map((v) => v.toLowerCase()) : values);
    }
    return lookupSets;
  };

  // ---- Actions -------------------------------------------------------------

  handleDownloadTemplate = async (item) => {
    this.resetImport();
    const cfg = DATASET_CONFIG[item.key];
    if (!cfg) {
      alert("", "info", t("import_center"), t("import_not_available"));
      return;
    }
    loading(true);
    try {
      const res = await fetch(cfg.templateAsset);
      const blob = await res.blob();
      saveAs(blob, cfg.templateFile);
    } catch (e) {
      alert("", "warning", t("warning"), t("download_failed"));
    }
    loading(false);
  };

  handleExport = async (item) => {
    this.resetImport();
    const cfg = DATASET_CONFIG[item.key];
    if (!cfg) {
      alert("", "info", t("import_center"), t("import_not_available"));
      return;
    }

    loading(true);
    const result = await GET(cfg.endpoint, { page_size: 100000, page: 0 });
    const data = result?.status ? result.data || [] : [];

    if (!data.length) {
      loading(false);
      alert("", "info", t("import_center"), t("export_no_data"));
      return;
    }

    try {
      // Build the export on top of the template so the layout matches the template.
      const res = await fetch(cfg.templateAsset);
      const ab = await res.arrayBuffer();
      const wb = XLSX.read(ab, { type: "array" });
      const ws = wb.Sheets[cfg.sheetName] || wb.Sheets[wb.SheetNames[0]];

      // Some data sets are nested (e.g. brands with children) -> flatten for a tabular export.
      const exportData = cfg.flattenForExport ? cfg.flattenForExport(data) : data;

      const headerRowIdx = cfg.headerRows - 1; // 0-based row of the column headers
      const firstDataRow = cfg.headerRows;     // 0-based row of the first data row
      const uidCol = cfg.columns.length;       // UID is appended right after the last template column
      const range = XLSX.utils.decode_range(ws["!ref"]);
      const lastCol = Math.max(range.e.c, uidCol);

      // 1) Clear the data area. Templates ship with placeholder rows (running numbers,
      //    "No" defaults, zeros) that would otherwise leak below the real data.
      for (let r = firstDataRow; r <= range.e.r; r++) {
        for (let c = 0; c <= lastCol; c++) {
          delete ws[XLSX.utils.encode_cell({ r, c })];
        }
      }

      // 2) Append a UID column header at the end of the header row.
      XLSX.utils.sheet_add_aoa(ws, [["UID"]], { origin: { r: headerRowIdx, c: uidCol } });

      // 3) Write the real company data; each row carries its own uid at the very end.
      exportData.forEach((row, i) => {
        const arr = cfg.toRow(row, i);
        arr[uidCol] = row.uid || ""; // uid lands in the column after the last template column
        XLSX.utils.sheet_add_aoa(ws, [arr], { origin: { r: firstDataRow + i, c: 0 } });
      });

      // 4) Grow the sheet range to cover the written data + the UID column.
      range.e.c = lastCol;
      range.e.r = Math.max(headerRowIdx, firstDataRow + exportData.length - 1);
      ws["!ref"] = XLSX.utils.encode_range(range);

      const out = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      saveAs(new Blob([out], { type: "application/octet-stream" }), cfg.exportFile);
    } catch (e) {
      alert("", "warning", t("warning"), t("download_failed"));
    }
    loading(false);
  };

  handleImport = (item) => {
    this.resetImport();
    const cfg = DATASET_CONFIG[item.key];
    if (!cfg) {
      alert("", "info", t("import_center"), t("import_not_available"));
      return;
    }
    this.setState({ active_key: item.key }, () => {
      if (this.fileInputRef.current) this.fileInputRef.current.click();
    });
  };

  handleFileSelected = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = ""; // allow re-selecting the same file
    if (!file) return;

    const cfg = DATASET_CONFIG[this.state.active_key];
    if (!cfg) return;

    loading(true);
    try {
      const ab = await file.arrayBuffer();
      const wb = XLSX.read(ab, { type: "array" });
      const ws = wb.Sheets[cfg.sheetName] || wb.Sheets[wb.SheetNames[0]];
      const raw = XLSX.utils.sheet_to_json(ws, {
        header: 1,
        range: cfg.headerRows, // start reading at the first data row
        defval: "",
      });
      const refMap = this.parseReference(wb, cfg);
      const lookupSets = await this.fetchLookupSets(cfg);
      let rows = this.validateRows(cfg, raw, refMap, lookupSets);
      if (cfg.postValidate) rows = cfg.postValidate(rows, lookupSets);

      if (!rows.length) {
        loading(false);
        alert("", "info", t("import_center"), t("no_rows_found"));
        return;
      }
      // Keep the overlay up until the modal has actually rendered, otherwise the
      // loader closes a frame before the modal appears and the screen flickers.
      this.setState({ import_modal: true, import_rows: rows, show_invalid_only: false }, () => loading(false));
    } catch (err) {
      loading(false);
      alert("", "warning", t("warning"), t("import_read_failed"));
    }
  };

  handleConfirmImport = async () => {
    const { active_key, import_rows } = this.state;
    const cfg = DATASET_CONFIG[active_key];
    if (!cfg) return;
    if (!import_rows.length || import_rows.some((r) => !r.valid)) return;

    // No bulk upload endpoint yet for this data set -> validated/previewed only.
    if (!cfg.importPath) {
      alert("", "info", t("import_center"), t("import_pending_backend"));
      return;
    }

    if (
      !(await alert("", "info", t("confirm"), t("confirm_import_detail"), t("confirm"), t("cancel")))
    ) {
      return;
    }

    // Strip per-row company_id — it is sent once at the top level of the body.
    let rows = import_rows.map((r) => {
      const { company_id, ...rest } = cfg.toPayload(r.cells); // eslint-disable-line no-unused-vars
      return rest;
    });
    // Order rows where chunk boundaries matter (e.g. brand parents before children).
    if (cfg.sortRowsForImport) rows = cfg.sortRowsForImport(rows);

    const total = rows.length;
    const chunks = [];
    for (let i = 0; i < total; i += IMPORT_CHUNK_SIZE) chunks.push(rows.slice(i, i + IMPORT_CHUNK_SIZE));

    // Drive an in-modal progress bar; the static-backdrop modal + disabled buttons
    // block every other action while it runs.
    this.setState({ importing: true, import_progress: 0, import_done: 0 });

    let created = 0;
    let updated = 0;
    let done = 0;
    let failMessage = "";
    let aborted = false;

    const sendChunk = async (chunk) => {
      if (aborted) return;
      const res = await POST(cfg.importPath, { company_id: company?.id || "", rows: chunk });
      if (!res?.status) {
        aborted = true; // stop sending further chunks once one fails
        if (!failMessage) failMessage = res?.message || t("import_failed");
        return;
      }
      const d = res.data || {};
      created += d.created ?? 0;
      updated += d.updated ?? 0;
      done = Math.min(done + chunk.length, total);
      this.setState({ import_done: done, import_progress: Math.round((done / total) * 100) });
    };

    // Worker pool: send up to `concurrency` chunks at once for speed. Data sets with
    // intra-file dependencies (brand) run one at a time to preserve parent->child order.
    const concurrency = cfg.sequentialImport ? 1 : Math.min(IMPORT_CONCURRENCY, chunks.length || 1);
    let next = 0;
    const worker = async () => {
      while (next < chunks.length && !aborted) {
        const myChunk = chunks[next++];
        await sendChunk(myChunk);
      }
    };
    await Promise.all(Array.from({ length: concurrency }, worker));

    this.setState({ importing: false });

    if (!failMessage) {
      this.resetImport();
      alert(
        "",
        "success",
        t("import_done"),
        `${t("import_created_count")}: ${created}  |  ${t("import_updated_count")}: ${updated}`
      );
    } else {
      // Chunks commit independently -> some rows may already be in. Report the partial
      // result (re-importing is an idempotent upsert by code).
      this.setState({ import_progress: 0, import_done: 0 });
      const partial =
        created + updated > 0
          ? `\n${t("import_created_count")}: ${created}  |  ${t("import_updated_count")}: ${updated}`
          : "";
      alert("", "warning", t("warning"), failMessage + partial);
    }
  };

  // ---- Render --------------------------------------------------------------

  render() {
    const { filter_search, data_sets, import_modal, import_rows, active_key, importing, import_progress, import_done, show_invalid_only } = this.state;

    const keyword = filter_search.trim().toLowerCase();
    const filtered = keyword
      ? data_sets.filter(
          (d) =>
            t(d.name).toLowerCase().includes(keyword) ||
            t(d.description).toLowerCase().includes(keyword) ||
            d.template.toLowerCase().includes(keyword)
        )
      : data_sets;

    const cfg = DATASET_CONFIG[active_key];
    const previewColumns = cfg ? cfg.columns.filter((c) => c.type !== "auto") : [];
    const invalidCount = import_rows.filter((r) => !r.valid).length;
    const validCount = import_rows.length - invalidCount;
    const canConfirm = import_rows.length > 0 && invalidCount === 0 && !importing;
    // Preview rows, tagged with their original 1-based row number so filtering to
    // error rows still shows the real position in the file.
    const previewRows = import_rows
      .map((row, ri) => ({ row, no: ri + 1 }))
      .filter((r) => !show_invalid_only || !r.row.valid);
    const activeName = data_sets.find((d) => d.key === active_key)?.name || "";

    return (
      <div className="w-100 min-vh-100" style={{ backgroundColor: "#F4F7FE" }}>
        <NavbarWarehouse />

        {/* Hidden file input for import */}
        <input
          ref={this.fileInputRef}
          type="file"
          accept=".xlsx,.xls"
          className="d-none"
          onChange={this.handleFileSelected}
        />

        <div className="body p-4 mt-3">
          {/* Header Section */}
          <div className="d-flex flex-wrap justify-content-between align-items-center mb-4 gap-3">
            <div>
              <h3 className="fw-bold mb-1 text-dark">{t("import_center")}</h3>
              <div className="text-secondary text-14">{t("import_center_desc")}</div>
            </div>
          </div>

          {/* Search Toolbar */}
          <div className="bg-white rounded-4 shadow-sm p-4 mb-4">
            <div className="d-flex align-items-center gap-3 flex-wrap">
              <label className="fw-bold text-dark text-14 mb-0">{t("search")}:</label>
              <div className="position-relative" style={{ maxWidth: "360px", width: "100%" }}>
                <input
                  type="text"
                  className="form-control wpx-300"
                  style={{ height: "38px" }}
                  placeholder={t("type_to_search")}
                  value={filter_search}
                  onChange={(e) => this.setState({ filter_search: e.target.value })}
                />
              </div>
            </div>
          </div>

          {/* Data Set Table */}
          <div className="bg-white rounded-4 shadow-sm mb-4 overflow-hidden">
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0" style={{ minWidth: "900px" }}>
                <thead>
                  <tr className="text-white fw-bold text-12 bg-primary">
                    <th className="py-3 border-0 ps-4">{t("data_set")}</th>
                    <th className="py-3 border-0 text-center">{t("template_file")}</th>
                    <th className="py-3 border-0 text-center pe-4 wpx-450">{t("actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 ? (
                    <tr>
                      <td colSpan="3" className="text-center text-secondary py-5">
                        {t("no_data")}
                      </td>
                    </tr>
                  ) : (
                    filtered.map((item, idx) => (
                      <tr key={idx} className="border-bottom border-light bg-white">
                        {/* Data Set Info */}
                        <td className="py-3 ps-4">
                          <div className="d-flex align-items-center">
                            <div
                              className={`wpx-35 hpx-35 rounded text-white d-flex align-items-center justify-content-center me-3 flex-shrink-0 fw-bold text-14 bg-${item.color}`}
                            >
                              <span className="icon">{item.icon}</span>
                            </div>
                            <div>
                              <div className="fw-bold text-dark text-15">{t(item.name)}</div>
                              <div className="text-secondary text-12">{t(item.description)}</div>
                            </div>
                          </div>
                        </td>

                        {/* Template File */}
                        <td className="text-center">
                          <span
                            className="badge bg-primary-light text-primary rounded-pill px-3 py-2 fw-bold text-12 pointer"
                            onClick={() => this.handleDownloadTemplate(item)}
                          >
                            {item.template}
                          </span>
                        </td>

                        {/* Actions */}
                        <td className="text-center pe-4">
                          <div className="d-flex flex-column align-items-end gap-1">
                            <div className="d-flex justify-content-end gap-2">
                              <button
                                className="btn btn-white border px-3 py-2 fw-bold rounded-3 shadow-sm d-flex align-items-center justify-content-center gap-2 text-13 wpx-120"
                                onClick={() => this.handleDownloadTemplate(item)}
                              >
                                <span className="icon">{""}</span> {t("template")}
                              </button>
                              <button
                                className="btn btn-white border px-3 py-2 fw-bold rounded-3 shadow-sm d-flex align-items-center justify-content-center gap-2 text-13 wpx-120"
                                onClick={() => this.handleExport(item)}
                              >
                                <span className="icon">{""}</span> {t("export")}
                              </button>
                              <button
                                className="btn btn-primary px-3 py-2 fw-bold rounded-3 shadow-sm d-flex align-items-center justify-content-center gap-2 text-13 wpx-120"
                                onClick={() => this.handleImport(item)}
                              >
                                <span className="icon">{""}</span> {t("import")}
                              </button>
                            </div>
                            <div className="text-success text-11 fw-bold">
                              {DATASET_CONFIG[item.key] ? t("ready_for_import") : t("coming_soon")}
                            </div>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* IMPORT PREVIEW MODAL */}
        <Modal
          size="xl"
          show={import_modal}
          onHide={importing ? undefined : this.resetImport}
          centered
          contentClassName="border-0 rounded-4 overflow-hidden shadow-lg"
        >
          <Modal.Header className="border-bottom border-light px-4 py-3 bg-white" closeButton={!importing}>
            <div>
              <h5 className="fw-bold mb-0 text-dark">{t("import_preview_title")}</h5>
              <div className="text-secondary text-12">{activeName ? t(activeName) : ""}</div>
            </div>
          </Modal.Header>

          <Modal.Body className="p-4 bg-white">
            {/* Import progress — chunks are posted with bounded concurrency. The static
                backdrop + disabled buttons block every other action meanwhile. */}
            {importing && (
              <div className="bg-primary-light rounded-3 p-3 mb-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="fw-bold text-primary text-13">
                    <span className="spinner-border spinner-border-sm me-2" role="status" />
                    {t("importing")}
                  </span>
                  <span className="fw-bold text-primary text-15">{import_progress}%</span>
                </div>
                <div className="progress" style={{ height: "10px" }}>
                  <div
                    className="progress-bar progress-bar-striped progress-bar-animated bg-primary"
                    role="progressbar"
                    style={{ width: `${import_progress}%`, transition: "width 0.3s ease" }}
                    aria-valuenow={import_progress}
                    aria-valuemin={0}
                    aria-valuemax={100}
                  />
                </div>
                <div className="text-primary text-11 fw-bold mt-2">
                  {import_done} / {import_rows.length} {t("rows")}
                </div>
              </div>
            )}

            {/* Summary */}
            <div className="d-flex flex-wrap gap-3 mb-3 align-items-center">
              <span className="badge bg-light text-dark border rounded-pill px-3 py-2 fw-bold text-12">
                {t("total_rows")}: {import_rows.length}
              </span>
              <span className="badge bg-success-light text-success rounded-pill px-3 py-2 fw-bold text-12">
                {t("rows_valid")}: {validCount}
              </span>
              <span
                className={`badge rounded-pill px-3 py-2 fw-bold text-12 ${
                  invalidCount > 0 ? "pointer" : ""
                } ${
                  show_invalid_only
                    ? "bg-danger text-white"
                    : "bg-danger-light text-danger"
                }`}
                onClick={() =>
                  invalidCount > 0 &&
                  this.setState({ show_invalid_only: !show_invalid_only })
                }
                title={invalidCount > 0 ? t("show_invalid_only") : ""}
              >
                {t("rows_invalid")}: {invalidCount}
                {invalidCount > 0 && (
                  <span className="ms-2">{show_invalid_only ? "✕" : "▼"}</span>
                )}
              </span>
              {show_invalid_only && (
                <span
                  className="badge bg-light text-dark border rounded-pill px-3 py-2 fw-bold text-12 pointer"
                  onClick={() => this.setState({ show_invalid_only: false })}
                >
                  {t("show_all_rows")}
                </span>
              )}
              <span className="text-secondary text-11 d-flex align-items-center">
                <span className="d-inline-block wpx-12 hpx-12 rounded bg-danger-light border border-danger me-2" />
                {t("legend_invalid")}
              </span>
            </div>

            {/* Preview Table */}
            <div className="table-responsive border rounded-3" style={{ maxHeight: "420px", overflow: "auto" }}>
              <table className="table table-bordered align-middle mb-0 text-13" style={{ minWidth: "900px" }}>
                <thead className="position-sticky top-0" style={{ zIndex: 1 }}>
                  <tr className="bg-light text-dark fw-bold text-12">
                    <th className="py-2 text-center bg-light" style={{ width: "60px" }}>#</th>
                    {previewColumns.map((c) => (
                      <th key={c.key} className="py-2 bg-light">
                        <div>{c.th}</div>
                        <div className="text-secondary text-10 fw-normal">{c.en}</div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {previewRows.length === 0 ? (
                    <tr>
                      <td colSpan={previewColumns.length + 1} className="text-center text-secondary py-4">
                        {t("no_data")}
                      </td>
                    </tr>
                  ) : (
                    previewRows.map(({ row, no }) => (
                    <tr key={no} className={row.valid ? "" : "bg-danger-subtle"}>
                      <td className="text-center text-secondary">{no}</td>
                      {previewColumns.map((c) => {
                        const cell = row.cells[c.key] || { raw: "", error: "" };
                        const hasError = !!cell.error;
                        return (
                          <td
                            key={c.key}
                            title={hasError ? this.errorText(cell.error) + (cell.detail ? `: ${cell.detail}` : "") : ""}
                            className={hasError ? "bg-danger-light text-danger fw-bold" : "text-dark"}
                          >
                            {cell.raw !== "" ? cell.raw : <span className="text-secondary">-</span>}
                            {hasError && (
                              <div className="text-danger text-10 fw-normal">
                                {this.errorText(cell.error)}
                                {cell.detail ? `: ${cell.detail}` : ""}
                              </div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </Modal.Body>

          <Modal.Footer className="border-top border-light px-4 py-3 bg-white d-flex justify-content-between align-items-center">
            <div className="text-secondary text-12">
              {importing
                ? t("importing")
                : invalidCount > 0
                ? t("fix_errors_first")
                : t("ready_to_import_hint")}
            </div>
            <div className="d-flex gap-2">
              <button
                className="btn btn-white border px-4 py-2 text-dark rounded-3 fw-bold"
                disabled={importing}
                onClick={this.resetImport}
              >
                {t("cancel")}
              </button>
              <button
                className="btn btn-primary px-4 py-2 d-flex align-items-center rounded-3 fw-bold text-white shadow-sm"
                disabled={!canConfirm}
                onClick={this.handleConfirmImport}
              >
                {importing && (
                  <span className="spinner-border spinner-border-sm me-2" role="status" />
                )}
                {importing ? (
                  `${import_progress}%`
                ) : (
                  <>
                    <span className="icon me-2">{""}</span> {t("confirm_import")}
                  </>
                )}
              </button>
            </div>
          </Modal.Footer>
        </Modal>
      </div>
    );
  }
}
